import { CoObjectItem } from "../../components/interfaces/CoObject";
import {
  confirmElaborationItemsResultAddAndUserChecked,
  confirmElaborationItemsResultAddSilent,
  notifyFailedElaboration,
  UpdateOptions,
} from "../../api/object/RefreshObject";
import { mapCoObjectToRefreshElaborationItem } from "../../redux/feature/cpm/map-objects";
import * as uuid from "uuid";
import {
  createElaborationRequestAsync,
  ElaborationMessage,
  ElaborationStatus,
  ElaborationStatusItem,
  elaborationStatusItemIsToAdd,
  elaborationStatusItemIsToHandleAbort,
  flagElaborationItemResultAdded,
  flagElaborationUserChecked,
  selectAbortedElaborations,
  selectElaborationsByCorrelationId,
  selectElaborationsWithNotRunningAndNotCheckedItems,
} from "../../redux/feature/object/remote-elaborations-slice";
import NotificationManager, { NotificationType } from "../../common/notification/NotificationManager";
import { formatMessage, formatMessageWithParams, tryFormatMessageWithJson } from "./LanguageHelper";
import { store } from "../../redux/store/store";
import { invalidate } from "../../redux/feature/validation/validation-slice";
import {
  handleCloneResult,
  handleDetachResult,
  handleRefreshComponentInstanceResult,
  updateContentControlWithElabResult,
} from "../../office/ContentControl";
import { abortRefreshAsync } from "../../redux/feature/cpm/cpm-slice";
import { TableUpdateStrategy } from "../../components/coobjects/co-panel/ObjectPanelComponent";
import { handleDownloadCopyResult } from "../../office/DownloadCopy";
import { addOriginalPayloadToCoObject, deleteContentControlById } from "./ContentControlHelper";
import { applyInheritedInfo } from "./InheritanceHelper";
import { DocumentPart, ElaborationRequest } from "../../api/models";
import { applyComponentDataToLegacyComponentInstance, isLegacyComponentInstance } from "./ComponentInstanceHelper";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import AsyncObjectUpdateManager from "../../common/update/AsyncObjectUpdateManager";
import { runSingletonOrWait } from "./SingletonHandler";
import { handleCheckDifferencesResult } from "../../office/ExportDifferences";
import { handleExportObjectsListResult } from "../../office/ExportObjectsList";
import { handleExportSubmissionHistoryResult } from "../../office/ExportSubmissionHistory";
import { handleDbMappingResult } from "../../office/DbMapping";
import RollForwardManager from "../../components/document/RollForwardManager";
import { isNotAvailableErrorText, isPermissionCopyErrorText } from "../../components/document/RollForwardPermission";
import { buildComponentMappingTerminalNotification } from "./ComponentMappingNotification";
import { stopComponentMappingLoading } from "./ComponentMappingLoading";

type OperationStatus = "success" | "failed" | "warning" | "aborted";

interface OperationResult {
  operationStatus: OperationStatus;
  notificationType: NotificationType;
}

function parseDateSafe(value?: string | null): number | null {
  if (!value) return null;
  // If your backend means UTC, ensure the string has 'Z'. Otherwise, consider appending 'Z' or using a library.
  // Example: return Date.parse(value.endsWith('Z') ? value : `${value}Z`);
  const t = Date.parse(value); // Will treat strings without 'Z' as local time!
  return Number.isNaN(t) ? null : t;
}

export const checkAbortable = (item: ElaborationStatusItem) => {
  return (
    item.elaborationType !== "CHANGE_DOC_STRUCTURE" &&
    item.elaborationType !== "DETACH" &&
    item.elaborationType !== "SHEET_AND_AREA" &&
    item.elaborationType !== "RENAME"
  );
};

export async function launchRefreshElaboration(
  username: string,
  coObjects: CoObjectItem[],
  documentPart: DocumentPart,
  documentPartMetadata: DocumentPartMetadata,
  selectedUpdateOption?: UpdateOptions,
  tableUpdateType?: TableUpdateStrategy["typeUpdate"]
) {
  const clientID = store.getState().configurationState.clientId;
  const correlationId = uuid.v4();

  if (!coObjects || coObjects.length == 0) {
    throw new Error("empty CoObjectItem array");
  }

  const coObjectsWithInstanceComponents = await Promise.all(
    coObjects.map(async (coObject) => {
      if (isLegacyComponentInstance(coObject)) {
        coObject = await applyComponentDataToLegacyComponentInstance(coObject, documentPartMetadata);
      } else {
        await applyInheritedInfo(coObject, documentPartMetadata);
      }
      return coObject;
    })
  );

  const elaborationRequest: ElaborationRequest = {
    correlationId: correlationId,
    clientId: clientID,
    documentId: documentPart.id,
    userId: username,
    tgkElaborationItemList: await Promise.all(
      coObjectsWithInstanceComponents.map((coObject) => {
        return mapCoObjectToRefreshElaborationItem(
          correlationId,
          documentPart,
          documentPartMetadata,
          coObject,
          selectedUpdateOption,
          tableUpdateType
        );
      })
    ),
  };

  const elaborationResponse = await createElaborationRequestAsync(elaborationRequest);
  console.log("elaborationResponse: ", elaborationResponse);
}

export const onConfirmElaboration = (elaboration: ElaborationStatus) => {
  void confirmElaborationItemsResultAddAndUserChecked(
    elaboration.correlationId,
    elaboration.items.map((item) => item.id),
    elaboration.items.map((item) => item.id)
  ).catch((err) => {
    console.warn(`[onConfirmElaboration] API call failed (auth expired or network error), ignoring: ${err}`);
  });
  store.dispatch(flagElaborationUserChecked({ correlationId: elaboration.correlationId }));
};

export function abortAbortableElaborationStatusItems(items: ElaborationStatusItem[]) {
  const correlationIds = new Set<string>();
  items.forEach((item) => {
    if (checkAbortable(item)) correlationIds.add(item.correlationId);
  });

  void abortRefreshAsync({
    correlationIds: Array.from(correlationIds),
  });
}

export const handleState = async () =>
  await runSingletonOrWait(async (isStale) => {
    const state = store.getState();
    let elaborationsToHandle = selectAbortedElaborations(state);

    if (!elaborationsToHandle?.length) {
      elaborationsToHandle = selectElaborationsWithNotRunningAndNotCheckedItems(state);
    }

    for (const elaboration of elaborationsToHandle) {
      if (isStale()) break;

      const pendingElaborationItems = elaboration.items.filter(
        (item) => elaborationStatusItemIsToAdd(item) || elaborationStatusItemIsToHandleAbort(item)
      );

      for (const item of pendingElaborationItems) {
        if (isStale()) {
          break;
        }
        await handlePendingElaboration(item, elaboration);
      }
      console.debug(
        `[handleState] Calling handleNotifications for elaboration with correlationId: ${elaboration.correlationId}`,
        elaboration,
        isStale()
      );
      handleNotifications(elaboration.correlationId);
    }
  });

// Elaboration types whose outcome is already notified by a dedicated flow, so the generic
// elaboration notification must be skipped to avoid showing the user two toasts for the same operation.
// COMPONENT_MAPPING is included for documentation even though its own dedicated branch above always returns
// first, so this list is never actually consulted for that type.
const ELABORATION_TYPES_WITH_DEDICATED_NOTIFICATION = [
  "SHEET_AND_AREA",
  "DB_MAPPING",
  "DOCUMENT_DB_LIST",
  "COMPONENT_MAPPING",
];

function getEvidentResult(elaboration: ElaborationStatus) {
  return ["ADD", "EDIT", "REFRESH", "REFRESH_COMPONENT_INSTANCE", "CHANGE_DOC_STRUCTURE", "CLONE"].includes(
    elaboration.items[0]?.elaborationType
  );
}

const handleNotifications = (correlationId: string) => {
  const state = store.getState();
  const elaboration = selectElaborationsByCorrelationId(state, correlationId);
  console.debug(
    `[handleNotifications] Handle notification for elaboration with correlationId: ${correlationId}`,
    elaboration
  );
  if (
    elaboration.items.every(
      (item) =>
        item.status !== "STARTED" &&
        item.status !== "REQUESTED" &&
        ((item.status !== "COMPLETED" && item.status !== "ABORTED") || item.resultAdded)
    )
  ) {
    let messages: { errors: ElaborationMessage[]; warnings: ElaborationMessage[]; infos: ElaborationMessage[] } = {
      errors: [],
      warnings: [],
      infos: [],
    };
    messages = elaboration.items
      .flatMap(
        (item) =>
          item.messages?.map((msg) => {
            return { ...msg, text: msg.text ?? item.objectName };
          }) ?? []
      )
      .reduce((status, msg) => {
        if (msg.severity === "ERROR") return { ...status, errors: [...status.errors, msg] };
        if (msg.severity === "WARNING") return { ...status, warnings: [...status.warnings, msg] };
        if (msg.severity === "INFO") return { ...status, infos: [...status.infos, msg] };
        return status;
      }, messages);

    const operationName = getOperationName(elaboration.items[0]?.elaborationType.toLowerCase());
    const operationResult = getOperationResult(elaboration, messages.errors, messages.warnings, messages.infos);
    const isOperationFailed = operationResult.operationStatus === "failed";
    const isSuccessWithInfo =
      operationResult.operationStatus === "success" && operationResult.notificationType === "info";

    if (elaboration.items[0]?.elaborationType === "COMPONENT_MAPPING") {
      const componentMappingItem = elaboration.items.find((item) => item.elaborationType === "COMPONENT_MAPPING");
      if (componentMappingItem) {
        stopComponentMappingLoading(correlationId);
        NotificationManager.addNotification(buildComponentMappingTerminalNotification(componentMappingItem));
        onConfirmElaboration(elaboration);
      }
      return;
    }

    // A roll forward (duplicate) that failed because the SharePoint user rights could not be copied is
    // handled by the dedicated roll forward flow: show its permission-warning modal (letting the user
    // re-run without copying the rights, or cancel) instead of the generic failure notification.
    const isRollForwardPermissionFailure =
      isOperationFailed &&
      elaboration.items[0]?.elaborationType === "DUPLICATE" &&
      RollForwardManager.hasDuplicatePayloadWithUserRights(elaboration.correlationId) &&
      elaboration.items.some(
        (item) =>
          item.status === "FAILED" &&
          (item.messages ?? []).some(
            (message) =>
              isPermissionCopyErrorText(message?.details) ||
              isPermissionCopyErrorText(message?.text) ||
              isNotAvailableErrorText(message?.details) ||
              isNotAvailableErrorText(message?.text)
          )
      );
    if (isRollForwardPermissionFailure) {
      onConfirmElaboration(elaboration);
      RollForwardManager.showPermissionWarning(elaboration.correlationId);
      return;
    }

    // TODO: This a temporary workaround to handle the rename/detach error that occurs when a document is open. IDS-4578, IDS-5723
    if (
      (elaboration.items[0].elaborationType === "RENAME" || elaboration.items[0].elaborationType === "DETACH") &&
      operationResult?.operationStatus === "failed" &&
      messages.errors[0]?.details.startsWith("ERROR-DOCUMENT-OPEN:")
    ) {
      const elabType = elaboration.items[0].elaborationType.toLowerCase();
      const docName = messages.errors[0].details.replace("ERROR-DOCUMENT-OPEN:", "");
      NotificationManager.addNotification({
        key: elaboration.correlationId,
        title: formatMessage(`cof.elaboration.${elabType}.title.failed`),
        message: formatMessageWithParams(`cof.elaboration.${elabType}.message.failed`, {
          startDate: elaboration.items[0].startDate,
          endDate: elaboration.items[0].endDate,
        }),
        type: "error",
        closable: true,
        onClose: () => {
          onConfirmElaboration(elaboration);
          for (const item of elaboration.items) {
            void handleFailedElaboration(item, elaboration);
          }
        },
        removeOnClose: false,
        details: [
          {
            text: null,
            details: formatMessageWithParams(`cof.elaboration.${elabType}.error.documentopen`, { docName: docName }),
            severity: "ERROR",
            traceId: messages.errors[0]?.traceId,
          },
        ],
        isTemporary: false,
      });
      return;
    }
    // -- END WORKAROUND IDS-4578, IDS-5723 --

    const details = messages.errors.concat(messages.warnings);
    // TODO WORKAROUND FOR IDS-4654 and IDS-4764 (see also workaround for bug IDS-4578 and story IDS-4674)
    const localizeDetailField = (value: string | undefined): string | undefined => {
      if (!value) return value;
      const jsonResult = tryFormatMessageWithJson(value);
      if (jsonResult !== value) return jsonResult;
      if (value.startsWith("cof.")) return formatMessage(value);
      return value;
    };
    details.forEach((detail) => {
      detail.text = localizeDetailField(detail.text);
      detail.details = localizeDetailField(detail.details);
    });
    // END WORKAROUND FOR IDS-4654 and IDS-4764

    let title = formatMessage(`cof.elaboration.${operationName}.title.${operationResult.operationStatus}`);

    if (ELABORATION_TYPES_WITH_DEDICATED_NOTIFICATION.includes(elaboration.items[0].elaborationType)) {
      onConfirmElaboration(elaboration);
    } else {
      const { firstStarted, lastEnded, haveAllTimestamps } = elaboration.items.reduce(
        (acc, el) => {
          const startTs = parseDateSafe(el.startDate);
          const endTs = parseDateSafe(el.endDate);

          if (startTs != null && (acc.firstStarted == null || startTs < acc.firstStarted)) {
            acc.firstStarted = startTs;
          }
          if (endTs != null && (acc.lastEnded == null || endTs > acc.lastEnded)) {
            acc.lastEnded = endTs;
          }

          acc.haveAllTimestamps = acc.haveAllTimestamps && startTs != null && endTs != null;
          return acc;
        },
        {
          firstStarted: null as number | null,
          lastEnded: null as number | null,
          haveAllTimestamps: true,
        }
      );

      let isShort = haveAllTimestamps && lastEnded != null && firstStarted != null && lastEnded - firstStarted < 5000;
      const isSuccess = ["success", "info"].includes(operationResult.notificationType);
      const isEvidentResult = getEvidentResult(elaboration);
      const isTemporary = isSuccess && isShort && isEvidentResult;
      let msg = formatMessageWithParams(
        isSuccessWithInfo && messages.infos[0].details && messages.infos[0].details.length > 0
          ? messages.infos[0].details
          : `cof.elaboration.${operationName}.message.${operationResult.operationStatus}`,
        {
          startDate: new Date(firstStarted).toISOString().slice(0, -1),
          endDate: new Date(lastEnded).toISOString().slice(0, -1),
        }
      );

      NotificationManager.addNotification({
        key: elaboration.correlationId,
        title: title,
        message: msg,
        type: operationResult.notificationType,
        closable: true,
        onClose: () => {
          onConfirmElaboration(elaboration);
          if (isOperationFailed) {
            for (const item of elaboration.items) {
              void handleFailedElaboration(item, elaboration);
            }
          }
        },
        removeOnClose: true,
        details: details,
        isTemporary: isTemporary,
      });
    }
  }
};

const handleFailedElaboration = async (item: ElaborationStatusItem, elaboration: ElaborationStatus) => {
  if (item.elaborationType !== "DETACH" || !item.contentControlId?.length) {
    return;
  }
  try {
    await deleteContentControlById(Number(item.contentControlId), false);
  } catch (er) {
    console.log(`error trying to handle failed elaboration with correlationId ${elaboration.correlationId}: ${er}`, {
      correlationId: elaboration.correlationId,
      originalElaboration: elaboration,
      originalError: er,
    });
  }
};

const handlePendingElaboration = async (item: ElaborationStatusItem, elaboration: ElaborationStatus) => {
  try {
    if (item.contentControlId?.length && item.status === "ABORTED" && item.elaborationType === "CLONE") {
      await deleteContentControlById(Number(item.contentControlId));
    } else if (item.status !== "ABORTED") {
      switch (item.elaborationType) {
        case "DETACH":
          await handleDetachResult(item);
          await handleDefaultElaboration(item);
          break;
        case "CLONE":
          await handleCloneResult(item);
          await handleDefaultElaboration(item);
          break;
        case "DOWNLOAD_COPY":
          await handleDownloadCopyResult(item);
          break;
        case "EXPORT_SUBMISSION_HISTORY":
          await handleExportSubmissionHistoryResult(item);
          break;
        case "CHECK_DIFFERENCES":
          await handleCheckDifferencesResult(item);
          break;
        case "ALIGN_METADATA":
          console.log("Completing align metadata elaboration");
          break;
        case "RENAME":
          console.log("Completing rename elaboration");
          break;
        case "REFRESH_COMPONENT_INSTANCE": {
          const updatedCoObject = await handleRefreshComponentInstanceResult(item);
          await handleDefaultElaboration(item);

          if (item.elaborationUpdateType === "SINGLE") {
            console.log("Invoke async object update");
            AsyncObjectUpdateManager.addUpdatedObject(updatedCoObject);
          }
          break;
        }
        case "EXPORT_OBJECT_LIST":
          await handleExportObjectsListResult(item);
          break;
        case "DOCUMENT_DB_LIST":
          console.log("Completing document db list elaboration");
          break;
        case "DB_MAPPING":
          await handleDbMappingResult(item);
          break;
        case "CHANGE_TEMPLATE":
          console.log("Completing change template elaboration");
          break;
        case "COMPONENT_MAPPING":
          break;
        default:
          await handleDefaultElaboration(item);
          break;
      }
    }

    void confirmElaborationItemsResultAddSilent(item.id);
    store.dispatch(flagElaborationItemResultAdded({ correlationId: elaboration.correlationId, itemId: item.id }));
  } catch (er) {
    console.log(`error trying to add content to document object with name ${item.objectName}: ${er}`, {
      itemId: item.id,
      correlationId: item.correlationId,
      objectName: item.objectName,
      originalItem: item,
      originalError: er,
    });
    await notifyFailedElaboration(item.id, er.toString());
  }
};

const getOperationResult = (
  elaboration: ElaborationStatus,
  errors: ElaborationMessage[],
  warnings: ElaborationMessage[],
  infos: ElaborationMessage[]
): OperationResult => {
  if (elaboration.items.some((item) => item.status === "ABORTED")) {
    return { operationStatus: "aborted", notificationType: "warning" };
  }

  if (elaboration.items.some((item) => item.status !== "COMPLETED")) {
    //rejected
    return { operationStatus: "failed", notificationType: "error" };
  }

  if (errors.length) {
    return { operationStatus: "failed", notificationType: "error" };
  }

  if (warnings.length) {
    return { operationStatus: "warning", notificationType: "warning" };
  }

  if (infos.length) {
    return { operationStatus: "success", notificationType: "info" };
  }

  return { operationStatus: "success", notificationType: "success" };
};

const getOperationName = (elaborationType?: string) => {
  let operationName: string;

  switch (elaborationType) {
    case "add":
      operationName = "insertobject";
      break;
    case "edit":
      operationName = "editobject";
      break;
    case "refresh":
      operationName = "updateobject";
      break;
    case "refresh_component_instance":
      operationName = "updateobject";
      break;
    case "change_doc_structure":
      operationName = "changedocumentstructure";
      break;
    case "align_metadata":
      operationName = "alignmetadata";
      break;
    case "send_to_indesign":
      operationName = "sendtoindesign";
      break;
    case "export_object_list":
      operationName = "exportobjects";
      break;
    case "export_submission_history":
      operationName = "exportsubmissionhistory";
      break;
    default:
      operationName = elaborationType;
      break;
  }

  console.log(operationName);
  return operationName;
};

// IDS-7107/IDS-7459: A document-part re-inclusion re-mints nested content-control ids.
// If the Validation panel is already open, its cached ids go stale, so force
// ValidationPanelHook to reload (dep `isLoaded`) once the content control has
// actually been updated with the re-included content. INTERNAL_REF_URL (and its
// header/footer preservation logic) is currently problematic and suspended, so
// document-part updates now go through REF_URL instead; keep both discriminators
// so the invalidation still fires regardless of which one is in use.
export const invalidateValidationCacheOnDocPartReinclusion = (item: ElaborationStatusItem): void => {
  if (item.elabResultType === "REF_URL" || item.elabResultType === "INTERNAL_REF_URL") {
    store.dispatch(invalidate());
  }
};

const handleDefaultElaboration = async (item: ElaborationStatusItem) => {
  console.time("WordJSElaborationHandling");
  const clientType = store.getState().configurationState.clientId;
  await updateContentControlWithElabResult(item, clientType === "WEB");
  invalidateValidationCacheOnDocPartReinclusion(item);
  await addOriginalPayloadToCoObject(item);
  console.timeEnd("WordJSElaborationHandling");
};
